import { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useFinancialYear } from "../context/FinancialYearContext";

const styles = {
  page: { fontSize: 11, padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3 },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  box: {
    border: "1px solid #e9ecef",
    borderRadius: 12,
    background: "#f8f9fa",
    padding: 10,
  },
  table: { fontSize: 11, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    color: "#fff",
  },
  td: { fontSize: 11, padding: "6px 8px" },
};

const n = (v) => Number(v || 0);
const money = (v) => n(v).toFixed(2);
const get = (row, a, b) => row?.[a] ?? row?.[b];

export default function YearClosingPage() {
  const { financialYear } = useFinancialYear();
  const { user } = useAuth();
  const [fromDate, setFromDate] = useState(financialYear.fromDate);
  const [toDate, setToDate] = useState(financialYear.toDate);
  const [remarks, setRemarks] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFromDate(financialYear.fromDate);
    setToDate(financialYear.toDate);
  }, [financialYear.fromDate, financialYear.toDate]);

  const totalIncome = useMemo(
    () =>
      rows
        .filter((r) => get(r, "AccountType", "accountType") === "Income")
        .reduce((s, r) => s + n(get(r, "Amount", "amount")), 0),
    [rows],
  );
  const totalExpense = useMemo(
    () =>
      rows
        .filter((r) => get(r, "AccountType", "accountType") === "Expense")
        .reduce((s, r) => s + n(get(r, "Amount", "amount")), 0),
    [rows],
  );
  const netProfit = totalIncome - totalExpense;
  const isClosed = rows.some((r) =>
    Boolean(get(r, "IsAlreadyClosed", "isAlreadyClosed")),
  );

  const preview = async () => {
    try {
      setLoading(true);
      const q = new URLSearchParams({ fromDate, toDate });
      if (user?.companyId) q.append("companyId", user.companyId);
      const res = await client.get(`/year-closing/preview?${q.toString()}`);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      alert("Failed to load year closing preview");
    } finally {
      setLoading(false);
    }
  };

  const closeYear = async () => {
    if (!rows.length) return alert("Load preview before closing the year.");
    if (isClosed) return alert("This financial year is already closed.");
    const ok = window.confirm(
      `Close financial year ${fromDate} to ${toDate}?\nNet ${netProfit >= 0 ? "Profit" : "Loss"}: ₹ ${money(Math.abs(netProfit))}\n\nThis will create a YEAR CLOSING journal voucher and transfer profit/loss to Retained Earnings.`,
    );
    if (!ok) return;
    try {
      setLoading(true);
      await client.post("/year-closing/close", {
        fromDate,
        toDate,
        companyId: user?.companyId || null,
        closedBy: user?.email || user?.fullName || "SYSTEM",
        remarks,
      });
      alert(
        "Financial year closed successfully. Balance Sheet will now show Retained Earnings instead of current year difference.",
      );
      await preview();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to close financial year");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    preview(); /* eslint-disable-next-line */
  }, []);

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-calendar-check me-2"></i>Year Closing
          </h6>
          <div style={styles.sub}>
            Close income and expense accounts and transfer current year
            profit/loss to Retained Earnings
          </div>
        </div>
        <div style={styles.body}>
          <div className="row g-2 align-items-end mb-2">
            <Field label="From Date" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Field>
            <Field label="To Date" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Field>
            <Field label="Remarks" className="col-md-4">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional closing remarks"
              />
            </Field>
            <div className="col-md-2 d-grid">
              <button
                className="btn btn-primary btn-sm"
                style={styles.btn}
                onClick={preview}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Preview"}
              </button>
            </div>
            <div className="col-md-2 d-grid">
              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={closeYear}
                disabled={loading || isClosed || !rows.length}
              >
                <i className="bi bi-lock me-1"></i>Close Year
              </button>
            </div>
          </div>

          <div className="row g-2 mb-2">
            <Stat title="Income" value={totalIncome} color="#198754" />
            <Stat title="Expense" value={totalExpense} color="#dc3545" />
            <div className="col-md-4">
              <div style={styles.box}>
                <div className="fw-bold" style={{ fontSize: 10 }}>
                  Net Result
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: netProfit >= 0 ? "#198754" : "#dc3545",
                  }}
                >
                  {netProfit >= 0 ? "Profit" : "Loss"}: ₹{" "}
                  {money(Math.abs(netProfit))}
                </div>
                <span
                  className={`badge ${isClosed ? "text-bg-secondary" : "text-bg-warning"}`}
                >
                  {isClosed ? "Already Closed" : "Open"}
                </span>
              </div>
            </div>
          </div>

          <div className="alert alert-info py-2" style={{ fontSize: 11 }}>
            Closing creates one journal voucher starting with <b>YCLOSE-</b>.
            Profit & Loss reports automatically exclude closing vouchers, while
            Balance Sheet uses Retained Earnings after closing.
          </div>

          <div
            style={{
              maxHeight: 390,
              overflow: "auto",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Account</th>
                  <th style={styles.th}>Type</th>
                  <th className="text-end" style={styles.th}>
                    Closing Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {!rows.length ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 24 }}
                    >
                      No income/expense records found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={i}>
                      <td style={styles.td}>
                        {get(r, "AccountCode", "accountCode")}
                      </td>
                      <td style={styles.td}>
                        {get(r, "AccountName", "accountName")}
                      </td>
                      <td style={styles.td}>
                        {get(r, "AccountType", "accountType")}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(get(r, "Amount", "amount"))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}
function Stat({ title, value, color }) {
  return (
    <div className="col-md-4">
      <div style={styles.box}>
        <div className="fw-bold" style={{ fontSize: 10, color }}>
          {title}
        </div>
        <div style={{ fontSize: 16, fontWeight: 800 }}>₹ {money(value)}</div>
      </div>
    </div>
  );
}
